import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DeleteForever,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import MuiChip from 'app/design/uiComponents/MuiChip';
import i18next from 'i18next';
import { useTheme } from '@mui/material/styles';
import SnakbarMessageModal from 'app/design/uiComponents/SnakbarMessageModal';
import MuiButton from 'app/design/uiComponents/MuiButton';
import FormattedMessage from 'app/design/uiComponents/FormattedMessage';
import { themeContext } from 'app/components/Context/ToggleThemeContext';
import {
  ChartSidebarProps,
  MarketData,
  Leg,
  Strategy,
  isString,
  isNumber,
  OptionChainTableRow,
  PositionData,
  StrikeData,
  isStrikeData,
  SymbolOptionChainData,
  PositionWithExpiryData,
} from './types';
import moment from 'moment';
import { updateGlobalPositions } from '../Datafeeds/datafeed';
import LegsSection from './LegsSection';
import OptionChainPopup from './OptionChainPopup';
import {
  convertPositionsToApiFormat,
  updateLocalStoragePositions,
  clearLocalStoragePositions,
  getCurrentLTP,
  calculateLegMTM,
  calculateMTM,
  handleLegDeleteWithSync,
  handleClearLegsWithSync,
  restorePositionsFromLocalStorage,
} from './legsUtils';

// Type declaration for TradingView widget
declare global {
  interface Window {
    tvWidget?: {
      activeChart?: () => {
        resetData?: () => void;
        symbol?: () => string;
        resolution?: () => string;
        getVisibleRange?: () => { from: number; to: number };
        setVisibleRange?: (range: { from: number; to: number }) => void;
      };
      setSymbol?: (
        symbol: string,
        resolution: string,
        callback?: () => void,
      ) => void;
    };
   
  }
}

// Styled components
const SidebarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 'calc(100vh - 90px)',
  backgroundColor: theme.palette.background.paper,
  overflowY: 'auto',
  padding: theme.spacing(2),
  minWidth: 280, // Minimum width to prevent cutoff
  boxSizing: 'border-box',
}));







const SymbolDropdownCard = styled(Card)(() => ({
  marginBottom: 16,
  '& .MuiCardContent-root': {
    padding: 12,
  },
}));

const StrategyCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
  '& .MuiCardContent-root': {
    padding: 8,
    textAlign: 'center',
  },
}));



// Default data
const defaultMarketData: MarketData[] = [
  {
    symbol: 'NIFTY',
    value: 24832.2,
    change: -219.7,
    changePercent: -0.88,
  },
  {
    symbol: 'INDIAVIX',
    value: 12.45,
    change: 0.23,
    changePercent: 1.88,
  },
];


const ChartSidebar: React.FC<ChartSidebarProps> = ({
  marketData = defaultMarketData,
  legs = [], // set default to empty array
  showUnderlying = true,
  onLegAdd,
  onLegEdit,
  onLegDelete,
  onClearLegs,
  onShowUnderlyingChange,
  // New props
  optionTabMetaData,
  setOptionTabMetaData,
  optionChainData,
  indexes = [],
  onSymbolChange,
  lotSize = 1,
  // Position persistence props
  positions = {},
  onPositionUpdate,
  optionChainMetadata,
  onPositionFlagTrigger,

}) => {
  const [expandedSections, setExpandedSections] = useState({
    marketData: true,
    legs: true,
    strategies: true,
  });

  // Popup state for option chain table
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // State for symbol change confirmation
  const [symbolChangeAlertOpen, setSymbolChangeAlertOpen] = useState(false);
  const [pendingSymbol, setPendingSymbol] = useState<string>('');

  // State for leg deletion confirmation
  const [legDeleteAlertOpen, setLegDeleteAlertOpen] = useState(false);
  const [pendingLegId, setPendingLegId] = useState<string>('');

  const handleSectionToggle = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

 


  const handleLegDeleteConfirm = (legId: string) => {
    setPendingLegId(legId);
    setLegDeleteAlertOpen(true);
  };

  const handleLegDeleteAlertClose = (success: boolean) => {
    

    if (success) {
      // User confirmed - delete the leg
      if (onLegDelete) {
        onLegDelete(pendingLegId);

        // Sync with localStorage after deleting leg
        const currentExpiry = optionTabMetaData?.optionExpiry;
    

        if (currentExpiry && positions[currentExpiry]) {
          // Find the leg to delete
          const legToDelete = legs.find(leg => leg.id === pendingLegId);
       

          if (legToDelete) {
            // Create position key
            const positionKey = `${legToDelete.contractType}_${legToDelete.strike}`;
           

            // Remove position from positions state
            const updatedPositionsForExpiry = { ...positions[currentExpiry] };
            delete updatedPositionsForExpiry[positionKey];
           

            // Update positions state
            const updatedPositions: PositionWithExpiryData = {
              ...positions,
              [currentExpiry]: updatedPositionsForExpiry,
            };
           

            // Update component state
            if (onPositionUpdate) {
              onPositionUpdate(updatedPositions);
              
            }

            // Update localStorage
            updateLocalStoragePositions(
              updatedPositions,
              safeSymbol,
              currentExpiry,
            );

            // Update global positions for chart
            const apiFormatObject = convertPositionsToApiFormat(
              updatedPositions,
              safeSymbol,
            );
            updateGlobalPositions(apiFormatObject.positions);


           

           
          } 
        }
      }
      setLegDeleteAlertOpen(false);
      setPendingLegId('');
    } else {
      // User cancelled - reset pending leg
      setLegDeleteAlertOpen(false);
      setPendingLegId('');
    
    }
  };

  const handleClearLegs = () => {
    if (onClearLegs && onPositionUpdate) {
      handleClearLegsWithSync(
        positions,
        optionTabMetaData,
        onClearLegs,
        onPositionUpdate,
        safeSymbol,
      );
    }
  };

  const handleShowUnderlyingChange = (show: boolean) => {
    if (onShowUnderlyingChange) {
      onShowUnderlyingChange(show);
    }
  };

  const handleSymbolChange = (symbol: string) => {
    // Check if there are any legs/positions
    if (legs.length > 0) {
      // Show confirmation dialog
      setPendingSymbol(symbol);
      setSymbolChangeAlertOpen(true);
    } else {
      // No legs, change symbol directly
      if (onSymbolChange) {
        onSymbolChange(symbol);
      }
      if (setOptionTabMetaData && optionTabMetaData) {
        // Reset expiry when symbol changes
        const newExpiry = optionChainMetadata?.options?.[symbol]?.[0] || '';
        setOptionTabMetaData({
          ...optionTabMetaData,
          symbol: symbol,
          optionExpiry: newExpiry,
        });
      }
    }
  };

  const handleAlertClose = (success: boolean) => {
    if (success) {
      // User confirmed - clear legs and change symbol
      if (onClearLegs) {
        onClearLegs();
      }

      // Clear positions state for old symbol
      if (onPositionUpdate) {
        const clearedPositions: PositionWithExpiryData = {};
        onPositionUpdate(clearedPositions);
      }

      // Clear positions for the old symbol from localStorage
      clearLocalStoragePositions(safeSymbol, optionTabMetaData);

      if (onSymbolChange) {
        onSymbolChange(pendingSymbol);
      }
      if (setOptionTabMetaData && optionTabMetaData) {
        // Reset expiry when symbol changes
        const newExpiry =
          optionChainMetadata?.options?.[pendingSymbol]?.[0] || '';
        setOptionTabMetaData({
          ...optionTabMetaData,
          symbol: pendingSymbol,
          optionExpiry: newExpiry,
        });
      }

      // Update global positions for chart (will be empty for new symbol)
      const apiFormatObject = convertPositionsToApiFormat({}, pendingSymbol);
      updateGlobalPositions(apiFormatObject.positions);

      setSymbolChangeAlertOpen(false);
      setPendingSymbol('');
    } else {
      // User cancelled - reset pending symbol
      setSymbolChangeAlertOpen(false);
      setPendingSymbol('');
    }
  };

  // Safe access to spot price with type guard
  const spotPrice = optionChainData?.spot_price;
  const safeSpotPrice = isNumber(spotPrice) ? spotPrice : 0;

  // Safe event handler for Select onChange
  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    if (isString(value)) {
      handleSymbolChange(value);
    }
  };

  // Safe event handler for expiry dropdown onChange
  const handleExpiryChange = useCallback((event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    if (isString(value) && setOptionTabMetaData && optionTabMetaData) {
      setOptionTabMetaData({
        ...optionTabMetaData,
        optionExpiry: value,
      });
    }
  }, [setOptionTabMetaData, optionTabMetaData]);

  // Safe access to optionTabMetaData symbol
  const currentSymbol = optionTabMetaData?.symbol;
  const safeSymbol = isString(currentSymbol) ? currentSymbol : 'NIFTY 50';

  // Safe access to indexes array
  const safeIndexes = Array.isArray(indexes) ? indexes : [];

  const positionsRef = useRef(positions);
  positionsRef.current = positions;

  const handleOptionClick = useCallback(
    (
      positionData: PositionData,
      contractType: string,
      strike: string,
      isMinusClick = false,
    ) => {
      const positionKey = `${contractType.toUpperCase()}_${strike}`;
      const currentExpiry = optionTabMetaData?.optionExpiry;

      if (!currentExpiry) {
        return; // No expiry selected
      }

      // Get existing positions for current expiry using ref
      const currentPositions = positionsRef.current;
      const existingPositionsForExpiry = currentPositions[currentExpiry] || {};
      const existingPosition = existingPositionsForExpiry[positionKey];

      let updatedPositionData = positionData;

      // If position exists, update it 
      if (existingPosition) {
        // If same direction, handle lots change
        if (existingPosition.direction === positionData.direction) {
          if (isMinusClick) {
            // This is a minus button click - decrease lots
            updatedPositionData = {
              ...positionData,
              lots: Math.max(1, existingPosition.lots - 1), // Decrease lots, minimum 1
            };
          } else {
            // This is a regular B/S button click - increase lots by 1
            updatedPositionData = {
              ...positionData,
              lots: existingPosition.lots + 1, // Add only 1 lot
            };
          }
        } else {
          // If different direction, change direction and reset lots to 1
          updatedPositionData = {
            ...positionData,
            lots: 1, // Reset to 1 lot
          };
        }
      } else {
        // New position, set lots to 1
        updatedPositionData = {
          ...positionData,
          lots: 1,
        };
      }

      // Create or update leg for parent component
      if (onLegAdd && optionTabMetaData?.optionExpiry) {
        const newLeg: Leg = {
          id: '', // Let parent component generate ID
          direction: updatedPositionData.direction,
          expiry: optionTabMetaData.optionExpiry,
          strike: strike,
          lots: updatedPositionData.lots,
          ltp: updatedPositionData.premium || 0,
          entryTime: updatedPositionData.entryTime || new Date().toISOString(),
          contractType: contractType,
          entryPrice: updatedPositionData.premium || 0,
          isActive: true,
          iv: updatedPositionData.iv,
          premium: updatedPositionData.premium,
          contractDetails: updatedPositionData.contractDetails,
        };
        onLegAdd(newLeg);
      }

      // Update positions for persistence - organize by expiry date
      if (onPositionUpdate) {
        const updatedPositions = {
          ...currentPositions,
          [currentExpiry]: {
            ...existingPositionsForExpiry,
            [positionKey]: updatedPositionData,
          },
        };

        onPositionUpdate(updatedPositions);
      }

      // Position update will trigger global position update through parent component
    },
    [onLegAdd, optionTabMetaData, onPositionUpdate],
  );

  const theme = useTheme();

  // Load positions from localStorage when component mounts or symbol/expiry changes
  useEffect(() => {
    if (safeSymbol && optionTabMetaData?.optionExpiry && onPositionUpdate && onLegAdd && onClearLegs) {
      try {
        restorePositionsFromLocalStorage(
          safeSymbol,
          optionTabMetaData,
          onPositionUpdate,
          onLegAdd,
          onClearLegs,
        );

        // Update global positions for chart using ref to avoid dependency issues
        const currentPositions = positionsRef.current;
        const apiFormatObject = convertPositionsToApiFormat(
          currentPositions,
          safeSymbol,
        );
        updateGlobalPositions(apiFormatObject.positions);
       
      } catch (error) {
      
        localStorage.removeItem('chartPositions');
      }
    }
  }, [
    safeSymbol,
    optionTabMetaData?.optionExpiry,
    onPositionUpdate,
    onLegAdd,
    onClearLegs,
    optionTabMetaData,
  ]);

 

  // Function to get current LTP for a leg
  const getCurrentLTPForLeg = (leg: Leg): number => {
    return getCurrentLTP(leg, optionChainData);
  };

  return (
    <SidebarContainer>
      {/* Symbol Selection Section */}
      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h6" fontWeight="bold">
            Symbol Selection
          </Typography>
          <IconButton
            size="small"
            onClick={() => handleSectionToggle('marketData')}
          >
            {expandedSections.marketData ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )}
          </IconButton>
        </Box>
        <Collapse in={expandedSections.marketData}>
          <SymbolDropdownCard variant="outlined" sx={{ border: 'none' }}>
            <CardContent>
              <Box display="flex" gap={1} flexDirection="column">
                {/* Symbol Dropdown */}
                <FormControl fullWidth>
                  <Select
                    labelId="symbol"
                    name="Symbol"
                    value={safeSymbol}
                    onChange={handleSelectChange}
                    renderValue={value => (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                      >
                        <Typography variant="body1" fontWeight="bold">
                          {value}
                        </Typography>
                        {safeSpotPrice > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            {safeSpotPrice.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    )}
                    sx={{
                      '& .MuiSelect-select': {
                        pt: '6px',
                        pb: '6px',
                      },
                    }}
                  >
                    {safeIndexes.map(index => (
                      <MenuItem key={index} value={index}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                          <Typography variant="body1">
                        {index}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Expiry Dropdown */}
                {optionChainMetadata?.options?.[safeSymbol] ? (
                  <FormControl fullWidth>
                    <Select
                      labelId="option-expiry"
                      name="Expiry Date"
                      value={optionTabMetaData?.optionExpiry || ''}
                      onChange={handleExpiryChange}
                      sx={{
                        '& .MuiSelect-select': {
                          pt: '6px',
                          pb: '6px',
                        },
                      }}
                      renderValue={selected => (
                        <Typography variant="body1" fontWeight="bold">
                          {selected
                            ? moment(selected).format('DD MMM YY')
                            : 'Select Expiry'}
                        </Typography>
                      )}
                    >
                      {optionChainMetadata.options[safeSymbol].map(
                        (expiry, i) => (
                          <MenuItem key={i} value={expiry}>
                            {moment(expiry).format('DD MMM YY')}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl fullWidth>
                    <Select
                      labelId="option-expiry"
                      name="Expiry Date"
                      value=""
                      disabled
                      sx={{
                        '& .MuiSelect-select': {
                          pt: '6px',
                          pb: '6px',
                        },
                      }}
                      renderValue={() => (
                        <Typography variant="body2" color="text.secondary">
                          Loading...
                        </Typography>
                      )}
                    >
                      <MenuItem value="">Loading...</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            </CardContent>
          </SymbolDropdownCard>
        </Collapse>
      </Box>

      {/* Legs Section */}
      <LegsSection
        legs={legs}
        expandedSections={expandedSections}
        onSectionToggle={handleSectionToggle}
        onClearLegs={handleClearLegs}
        onLegDeleteConfirm={handleLegDeleteConfirm}
        onAddEditClick={() => setIsPopupOpen(true)}
        getCurrentLTP={getCurrentLTPForLeg}
        handleShowUnderlyingChange={handleShowUnderlyingChange}
        showUnderlying={showUnderlying}
      />

      {/* Option Chain Popup Dialog */}
      <OptionChainPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        safeSymbol={safeSymbol}
        safeSpotPrice={safeSpotPrice}
        optionChainData={optionChainData}
        positions={positions}
        optionTabMetaData={optionTabMetaData}
        onClearLegs={onClearLegs}
        onLegAdd={onLegAdd}
        onPositionUpdate={onPositionUpdate}
        handleOptionClick={handleOptionClick}
        onPositionFlagTrigger={onPositionFlagTrigger}

      />

      {/* Symbol Change Confirmation Alert */}
      <SnakbarMessageModal
        open={symbolChangeAlertOpen}
        handleClose={() => handleAlertClose(false)}
        body={
          <Box>
            <Typography variant="body1" sx={{ pb: 1, fontWeight: 600 }}>
              Change Symbol?
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              You have {legs.length} active leg{legs.length !== 1 ? 's' : ''}.
              Changing the symbol will clear all your positions. Are you sure
              you want to continue?
            </Typography>
          </Box>
        }
        footer={
          <>
            <MuiButton
              fullWidth
              variant="contained"
              color="error"
              sx={{ mb: 2 }}
              onClick={() => handleAlertClose(true)}
            >
              Continue & Clear Positions
            </MuiButton>
            <MuiButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => handleAlertClose(false)}
            >
              Cancel
            </MuiButton>
          </>
        }
      />

      {/* Leg Deletion Confirmation Alert */}
      <SnakbarMessageModal
        open={legDeleteAlertOpen}
        handleClose={() => handleLegDeleteAlertClose(false)}
        body={
          <Box>
            <Typography variant="body1" sx={{ pb: 1, fontWeight: 600 }}>
              Delete Leg?
            </Typography>
            <Typography variant="body2">
              Are you sure you want to delete this leg? This action cannot be
              undone.
            </Typography>
          </Box>
        }
        footer={
          <>
            <MuiButton
              fullWidth
              variant="contained"
              color="error"
              sx={{ mb: 2 }}
              onClick={() => handleLegDeleteAlertClose(true)}
            >
              Delete Leg
            </MuiButton>
            <MuiButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => handleLegDeleteAlertClose(false)}
            >
              Cancel
            </MuiButton>
          </>
        }
      />
    </SidebarContainer>
  );
};

export default ChartSidebar;
